/**
 * Onboarding guiado: o caminho por onde um cliente novo entra na plataforma.
 *
 * Seis passos em ordem, cada um dependendo do anterior. A forma e de ASSISTENTE, nao de abas: uma
 * trilha vertical a esquerda com o estado de cada passo, o passo atual a direita, e "Anterior /
 * Proximo" no rodape. Aba sugere conteudo independente, que se visita em qualquer ordem; aqui o
 * passo 4 nao funciona sem o 3, e a tela tem de dizer isso ANTES de a pessoa tentar.
 *
 * Tres perguntas que a trilha responde sem abrir passo nenhum:
 *
 * - o que ja esta feito (a de quem volta dias depois);
 * - o que esta bloqueado e POR QUE - "depende do passo 3", nao so um item apagado;
 * - quanto falta, no contador do topo.
 *
 * O cliente fica no topo, e nao dentro de cada passo: a listagem do backend ja e filtrada pelo
 * tenant da sessao, entao na pratica existe UM cliente. Repetir o seletor em cinco passos sugeria
 * uma escolha que nao existe.
 */

import { Button } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { RESOURCE_CATALOGO, RESOURCE_CONEXOES, RESOURCE_POLICIES } from '../api/types'
import { fetchSnapshot } from '../api/onboarding'
import { fetchMe } from '../auth/api'
import { usePermissoes } from '../auth/permissoes'
import { Alert } from '../components/Alert'
import { Skeleton } from '../components/Skeleton'
import { humanizar } from '../errors/humanizar'
import { CampoSelecao } from './campos'
import {
  PassoCliente,
  PassoConexao,
  PassoDataset,
  PassoHospital,
  usePassosCumpridos,
  type PassoProps,
} from './PassosCadastro'
import { PassoPolicy, PassoReport } from './PassosPublicacao'

type Estado = ReturnType<typeof usePassosCumpridos>

interface Passo {
  id: string
  titulo: string
  /** Uma linha na trilha: o que este passo resolve. */
  resumo: string
  /** Resource do Auth que o passo ESCREVE. Sem `manage` nele, o formulario nao aparece. */
  recurso: string
  /** Ja foi cumprido alguma vez. Nao impede refazer - so informa. */
  cumprido: (estado: Estado) => boolean
  /** O que falta ANTES deste passo. Ausente = nao depende de nada. */
  bloqueio?: (estado: Estado) => string | null
  conteudo: (props: PassoProps) => ReactNode
}

const PASSOS: Passo[] = [
  {
    id: 'cliente',
    titulo: 'Cliente',
    resumo: 'Espelha o tenant do Auth',
    recurso: RESOURCE_CATALOGO,
    cumprido: (estado) => estado.temCliente,
    conteudo: (props) => <PassoCliente {...props} />,
  },
  {
    id: 'hospital',
    titulo: 'Hospital',
    resumo: 'Espelha uma organization (opcional)',
    recurso: RESOURCE_CATALOGO,
    cumprido: (estado) => estado.temHospital,
    bloqueio: (estado) => (estado.temCliente ? null : 'Depende do passo 1'),
    conteudo: (props) => <PassoHospital {...props} />,
  },
  {
    id: 'conexao',
    titulo: 'Conexão',
    resumo: 'Service principal do Power BI',
    recurso: RESOURCE_CONEXOES,
    cumprido: (estado) => estado.temConexao,
    bloqueio: (estado) => (estado.temCliente ? null : 'Depende do passo 1'),
    conteudo: (props) => <PassoConexao {...props} />,
  },
  {
    id: 'dataset',
    titulo: 'Dataset',
    resumo: 'O semantic model e suas roles',
    recurso: RESOURCE_CATALOGO,
    cumprido: (estado) => estado.temDataset,
    bloqueio: (estado) =>
      !estado.temCliente ? 'Depende do passo 1' : estado.temConexao ? null : 'Depende do passo 3',
    conteudo: (props) => <PassoDataset {...props} />,
  },
  {
    id: 'report',
    titulo: 'Relatório',
    resumo: 'O relatório e o workspace que dá acesso',
    recurso: RESOURCE_CATALOGO,
    cumprido: () => false,
    bloqueio: (estado) =>
      !estado.temCliente ? 'Depende do passo 1' : estado.temConexao ? null : 'Depende do passo 3',
    conteudo: (props) => <PassoReport {...props} />,
  },
  {
    id: 'policy',
    titulo: 'Política de RLS',
    resumo: 'Quem vê quais linhas',
    recurso: RESOURCE_POLICIES,
    cumprido: () => false,
    bloqueio: (estado) => (estado.temDataset ? null : 'Depende de um relatório com dataset'),
    conteudo: (props) => <PassoPolicy {...props} />,
  },
]

export function OnboardingPage() {
  const [passo, setPasso] = useState('cliente')
  const [clienteEscolhido, setClienteEscolhido] = useState('')
  const { pode, carregando: permissoesCarregando } = usePermissoes()
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const tenantId = me.data?.tenant_id ?? ''

  const snapshot = useQuery({
    queryKey: ['snapshot', tenantId],
    queryFn: () => fetchSnapshot(tenantId),
    enabled: tenantId !== '',
    // O Auth e a fonte de verdade e muda por fora deste app (alguem cria uma organization la).
    // Meia hora e o intervalo em que uma sessao de onboarding cabe sem consultar de novo.
    staleTime: 30 * 60 * 1000,
  })

  const estado = usePassosCumpridos()

  if (snapshot.isPending || estado.carregando) return <Skeleton height={420} />
  if (snapshot.error !== null) {
    return (
      <Alert tone="danger">
        Não foi possível ler o Auth: {humanizar(snapshot.error)}. Sem a hierarquia do Auth
        (organizations, workspaces e profiles) não há como mapear nada — o onboarding depende dela.
      </Alert>
    )
  }
  if (estado.erro !== null) return <Alert tone="danger">{humanizar(estado.erro)}</Alert>

  const indice = Math.max(
    0,
    PASSOS.findIndex((item) => item.id === passo),
  )
  const atual = PASSOS[indice]
  if (atual === undefined) return null

  const cliente = estado.clientes.find((item) => item.id === clienteEscolhido) ?? estado.clientes[0]
  const concluidos = PASSOS.filter((item) => item.cumprido(estado)).length
  const bloqueio = atual.bloqueio?.(estado) ?? null
  const podeEscrever = permissoesCarregando || pode(atual.recurso, 'manage')

  return (
    <>

      {estado.clientes.length > 1 ? (
        <div className="campo--filtro">
          <CampoSelecao
            aoMudar={setClienteEscolhido}
            id="onboarding-cliente"
            opcoes={estado.clientes.map((item) => ({ valor: item.id, rotulo: item.name }))}
            rotulo="Cliente"
            valor={cliente?.id ?? ''}
          />
        </div>
      ) : null}

      <div className="assistente">
        <nav aria-label="Passos do onboarding" className="trilha">
          <p className="trilha__progresso">
            {concluidos} de {PASSOS.length} com registro
          </p>
          <ol>
            {PASSOS.map((item, posicao) => {
              const feito = item.cumprido(estado)
              const travado = item.bloqueio?.(estado) ?? null
              const ativo = item.id === atual.id
              return (
                <li key={item.id}>
                  <button
                    aria-current={ativo ? 'step' : undefined}
                    className={`trilha__item${ativo ? ' trilha__item--atual' : ''}${
                      travado === null ? '' : ' trilha__item--travado'
                    }`}
                    onClick={() => setPasso(item.id)}
                    type="button"
                  >
                    <span className={`trilha__numero${feito ? ' trilha__numero--feito' : ''}`}>
                      {feito ? '✓' : posicao + 1}
                    </span>
                    <span className="trilha__textos">
                      <span className="trilha__titulo">{item.titulo}</span>
                      <span className="trilha__resumo">{travado ?? item.resumo}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>

          <p className="trilha__nota">
            Dois passos acontecem <strong>fora daqui</strong>: criar tenant, organizations,
            workspaces e profiles é no <strong>Auth Manager</strong>, antes de começar; conferir se
            a RLS filtra o que devia é no <strong>Report Viewer</strong>, entrando como o usuário
            final.
          </p>
        </nav>

        <section className="painel assistente__painel">
          <header className="assistente__cabecalho">
            <div>
              <p className="assistente__posicao">
                Passo {indice + 1} de {PASSOS.length}
              </p>
              <h2 className="painel__titulo">{atual.titulo}</h2>
            </div>
            {atual.cumprido(estado) ? <span className="passo__marca">já feito</span> : null}
          </header>

          {bloqueio !== null ? (
            <Alert tone="warning">
              {bloqueio}. Conclua o passo anterior primeiro — o backend recusaria a criação de
              qualquer forma, e a recusa dele não explica tão bem quanto esta frase.
            </Alert>
          ) : !podeEscrever ? (
            <Alert tone="warning">
              Seu perfil alcança a leitura, mas não a criação neste passo. Quem concede isso é o
              administrador do Auth — e a recusa vale também para quem chamar a API direto, não só
              para esta tela.
            </Alert>
          ) : (
            atual.conteudo({ snapshot: snapshot.data, tenantId, cliente })
          )}

          <footer className="assistente__navegacao">
            <Button
              disabled={indice === 0}
              onClick={() => setPasso(PASSOS[indice - 1]?.id ?? atual.id)}
              variant="ghost"
            >
              ← Anterior
            </Button>
            <Button
              disabled={indice === PASSOS.length - 1}
              onClick={() => setPasso(PASSOS[indice + 1]?.id ?? atual.id)}
            >
              Próximo →
            </Button>
          </footer>
        </section>
      </div>
    </>
  )
}
